import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { hasRole, useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { currentTheme, setTheme, type Theme } from '../lib/theme';
import { toast } from './toasts';

// The account menu at the foot of the sidebar (as in Explee): who you are, the pages you visit rarely, the theme.

const ITEMS: Array<{ to: string; label: string; badge?: string }> = [
  { to: '/settings', label: 'Team and account' }, { to: '/integrations', label: 'Integrations' }, { to: '/developers', label: 'API keys' }, { to: '/billing', label: 'Billing' },
  { to: '/refer', label: 'Refer and earn', badge: 'Get $29' }, { to: '/notifications', label: 'Notifications' },
  // agency mode: the clients dashboard (or turning it on); owners and admins, outside a client workspace
  { to: '/agency', label: 'Agency' },
  { to: '/audit', label: 'Audit log' }, { to: '/kill-switches', label: 'Stop all spending' }, { to: '/mfa', label: 'Two-factor' },
];

export function UserMenu() {
  const { session, me, signOut, refresh } = useAuth();
  const [switching, setSwitching] = useState(false);
  const nx = Boolean(me?.org.new_experience);
  const flip = async () => {
    setSwitching(true);
    try { await api('/portal/experience', { method: 'PUT', body: { new_experience: !nx } }); await refresh(); }
    catch (e) { toast(`Could not switch the experience: ${(e as Error).message}`, undefined, undefined, true); }
    finally { setSwitching(false); }
  };
  const [open, setOpen] = useState(false);
  const [theme, setT] = useState<Theme>(currentTheme);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const email = session?.user.email ?? '';
  const pick = (t: Theme) => { setTheme(t); setT(t); };
  return (
    <div className="user-menu" ref={box}>
      {open ? (
        <div className="um-pop" role="menu">
          <div className="um-email">{email}</div>
          {ITEMS.filter((i) => (me || i.to === '/mfa') && (i.to !== '/agency' || (hasRole(me, 'owner', 'admin') && !me?.org.agency_org_id && !me?.org.sandbox_of))).map((i) => (
            <Link key={i.to} role="menuitem" to={i.to} className="um-item" onClick={() => setOpen(false)}>
              <span>{i.label}</span>{i.badge ? <span className="um-badge">{i.badge}</span> : null}
            </Link>
          ))}
          <button type="button" role="menuitem" className="um-item" onClick={() => { setOpen(false); window.dispatchEvent(new Event('olv-open-support')); }}>Help</button>
          {me && hasRole(me, 'owner', 'admin') ? (
            <button type="button" role="menuitemcheckbox" aria-checked={nx} className="um-item" disabled={switching} onClick={() => void flip()}>
              <span className="um-label">New experience<small className="muted">Chat-first, for the whole workspace</small></span><span className={`switch um-switch${nx ? ' on' : ''}`} aria-hidden="true"><span /></span>
            </button>
          ) : null}
          <div className="um-theme"><span>Theme</span>
            <div className="seg" role="radiogroup" aria-label="Theme">
              {([['light', 'Light', 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4'],
                [null, 'System', 'M3 5h18v11H3zM8 20h8M12 16v4'], ['dark', 'Dark', 'M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z']] as Array<[Theme, string, string]>).map(([t, label, d]) => (
                <button key={label} type="button" role="radio" aria-checked={theme === t} aria-label={label} title={label} onClick={() => pick(t)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
                </button>
              ))}
            </div>
          </div>
          <button type="button" role="menuitem" className="um-item danger" onClick={() => void signOut()}>Sign out</button>
        </div>
      ) : null}
      <button type="button" className="um-trigger" aria-haspopup="menu" aria-expanded={open} onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
        <span className="um-avatar" aria-hidden="true">{(email[0] ?? '?').toUpperCase()}</span>
        <span className="um-who"><b>{email.split('@')[0]}</b><small>{email}</small></span>
        <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 9l4-4 4 4M8 15l4 4 4-4" /></svg>
      </button>
    </div>
  );
}
